import type { ReactNode, FC } from "react";
import { useEffect, useState } from "react";
import { FiChevronDown, FiChevronRight } from "react-icons/fi";
import { COLOR_MAP } from "./menu.config";
import { IconBadge } from "./IconBadge";

export const SidebarGroup: FC<{
  label: string;
  icon?: ReactNode;
  children: ReactNode;
  defaultOpen?: boolean;
  active?: boolean;
  colorKey?: keyof typeof COLOR_MAP | null;
  disabled?: boolean;
}> = ({ label, icon, children, defaultOpen = false, active = false, colorKey, disabled }) => {
  const c = colorKey ? COLOR_MAP[colorKey] : null;
  const [open, setOpen] = useState<boolean>(defaultOpen);
  const isDisabled = !!disabled;

  const headerClasses = `flex w-full items-center justify-between rounded-lg px-3 py-2 text-left text-sm ${
    isDisabled
      ? "text-gray-400 bg-gray-50 cursor-not-allowed opacity-60"
      : active
      ? c
        ? `${c.activeText} ${c.activeBg}`
        : "text-sky-700 bg-sky-50"
      : "text-gray-800 hover:bg-gray-100"
  }`;

  // Keep group open state in sync if route changes and group becomes active
  useEffect(() => {
    if (active && !isDisabled) {
      setOpen(true);
    }
  }, [active, isDisabled]);

  return (
    <div>
      <button
        className={headerClasses}
        onClick={() => {
          if (isDisabled) return;
          setOpen((o) => !o);
        }}
        aria-disabled={isDisabled}
        aria-expanded={!isDisabled ? open : undefined}
        aria-controls={`group-${label.replace(/\s+/g, "-").toLowerCase()}`}
      >
        <span className="flex items-center gap-3">
          <IconBadge colorKey={colorKey || null}>{icon}</IconBadge>
          <span className={active && !isDisabled ? "font-medium" : ""}>{label}</span>
        </span>
        {!isDisabled && (open ? <FiChevronDown className="text-current" /> : <FiChevronRight className="text-current" />)}
      </button>

      {!isDisabled && open && (
        <div
          id={`group-${label.replace(/\s+/g, "-").toLowerCase()}`}
          className="ml-4 md:ml-6 mt-1 space-y-1 border-l border-gray-200 pl-2"
        >
          {children}
        </div>
      )}
    </div>
  );
};
